/**
 * Auth Method Card Component
 *
 * Reusable card for displaying authentication method options.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface AuthMethodCardProps {
  /** Icon to display */
  icon: ReactNode;
  /** Method title */
  title: string;
  /** Method description */
  description: string;
  /** Whether the card is selected */
  isSelected?: boolean;
  /** Whether the card is disabled */
  disabled?: boolean;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Click handler */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Auth Method Card
 *
 * Displays an authentication method option with icon, title, and description.
 * Supports selected, disabled, and loading states.
 */
export default function AuthMethodCard({
  icon,
  title,
  description,
  isSelected = false,
  disabled = false,
  isLoading = false,
  onClick,
  className,
}: AuthMethodCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn(
        'w-full flex items-center p-4 border rounded-lg text-left transition-all',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        isSelected
          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
        disabled && 'opacity-50 cursor-not-allowed',
        isLoading && 'cursor-wait',
        className
      )}
      aria-pressed={isSelected}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg',
          isSelected ? 'bg-primary-100' : 'bg-gray-100'
        )}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-5 w-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          icon
        )}
      </div>

      {/* Content */}
      <div className="ml-4 flex-1">
        <p
          className={cn(
            'text-sm font-medium',
            isSelected ? 'text-primary-700' : 'text-gray-900'
          )}
        >
          {title}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>

      {/* Arrow/indicator */}
      <div className="ml-4 flex-shrink-0">
        {isSelected ? (
          <svg
            className="w-5 h-5 text-primary-600"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
    </button>
  );
}
